import type { LevelInfo, Progression, RewardBundle } from '@vm/shared';

/** Account level cap. XP earned past the cap turns into prestige stars (cosmetic). */
export const MAX_LEVEL = 100;
/** XP per prestige star once the level cap is reached. */
export const PRESTIGE_XP = 25000;

const roundTo = (v: number, step: number): number => Math.round(v / step) * step;

/**
 * XP needed to advance from `level` to `level + 1`. Smooth quadratic growth rounded to 50:
 * 400 (1→2), ~1400 at 10, ~7700 at 50, ~24600 at 99. Level 100 needs ≈ 843k XP in total.
 */
export function xpToNext(level: number): number {
  if (level >= MAX_LEVEL) return PRESTIGE_XP;
  const l = Math.max(1, Math.floor(level)) - 1;
  return roundTo(400 + 100 * l + l * l, 50);
}

/** LEVEL_XP[L] = total XP required to reach level L (LEVEL_XP[1] = 0). Index 0 is unused. */
export const LEVEL_XP: readonly number[] = (() => {
  const out = [0, 0];
  for (let l = 1; l < MAX_LEVEL; l++) out.push(out[l] + xpToNext(l));
  return out;
})();

/** Account level (1..MAX_LEVEL) for a total XP amount. */
export function levelFromXp(xp: number): number {
  let lo = 1;
  let hi = MAX_LEVEL;
  const v = Math.max(0, Math.floor(xp));
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (LEVEL_XP[mid] <= v) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

/** Full progression view (level, XP inside the level, prestige) for a total XP amount. */
export function progressionFromXp(xp: number): Progression {
  const total = Math.max(0, Math.floor(xp));
  const level = levelFromXp(total);
  if (level >= MAX_LEVEL) {
    const over = total - LEVEL_XP[MAX_LEVEL];
    return { level, xp: total, xpInLevel: over % PRESTIGE_XP, xpToNext: PRESTIGE_XP, prestige: Math.floor(over / PRESTIGE_XP) };
  }
  return { level, xp: total, xpInLevel: total - LEVEL_XP[level], xpToNext: xpToNext(level), prestige: 0 };
}

/**
 * Cosmetic items handed out at milestone levels (coin-shop cosmetics, plus the Gilded armor for
 * reaching the cap). Already-owned items are compensated (see `duplicateCompensation`).
 */
export const LEVEL_ITEM_REWARDS: Readonly<Record<number, NonNullable<RewardBundle['items']>>> = {
  3: [{ type: 'armor', id: 'urban' }],
  8: [{ type: 'skin', id: 'medic' }],
  10: [{ type: 'petstyle', id: 'candy' }],
  12: [{ type: 'armor', id: 'desert' }],
  20: [{ type: 'skin', id: 'engineer' }],
  30: [{ type: 'armor', id: 'arctic' }],
  40: [{ type: 'skin', id: 'ninja' }],
  50: [{ type: 'armor', id: 'scout' }],
  100: [{ type: 'armor', id: 'gold' }],
};

/** Reward for reaching `level` (2..MAX_LEVEL): coins every level, gems every 5th, items at milestones. */
export function levelReward(level: number): RewardBundle {
  if (level < 2 || level > MAX_LEVEL) return {};
  const reward: RewardBundle = { coins: roundTo(100 + 20 * level, 10) };
  const gems = level % 10 === 0 ? 25 : level % 5 === 0 ? 10 : 0;
  if (gems) reward.gems = gems;
  const items = LEVEL_ITEM_REWARDS[level];
  if (items?.length) reward.items = items.map((i) => ({ ...i }));
  return reward;
}

/** Level table for the catalog: XP to the next level and the reward for reaching each level. */
export function levelTable(): LevelInfo[] {
  const out: LevelInfo[] = [];
  for (let l = 1; l <= MAX_LEVEL; l++) out.push({ level: l, xpToNext: l >= MAX_LEVEL ? 0 : xpToNext(l), rewards: levelReward(l) });
  return out;
}
